import React from 'react';
import { Link } from 'react-router-dom';
import { Restaurant } from '../types';
import { generateRestaurantSlug } from '../utils/slugify';

interface RestaurantCardProps {
  restaurant: Restaurant;
}

function restaurantLink(restaurant: Restaurant): string {
  // Prefer the SEO-friendly name slug, but fall back to the canonical
  // place_id route if the name can't produce a valid slug (e.g. a name with
  // no Latin characters), which would otherwise throw and blank the page.
  try {
    return `/restaurant/${generateRestaurantSlug(restaurant.name)}`;
  } catch {
    return `/place/${restaurant.placeId}`;
  }
}

const RestaurantCard: React.FC<RestaurantCardProps> = ({ restaurant }) => {
  const isTopPick = restaurant.rank === 1;

  return (
    <Link
      to={restaurantLink(restaurant)}
      className={`group relative block ${isTopPick ? 'py-8' : 'py-6'} border-b border-black/5 last:border-b-0 cursor-pointer`}
    >
      <div className="flex items-start gap-6 md:gap-10">
        {/* Rank Number */}
        <div className="flex-shrink-0 w-16 md:w-24">
          <span className={`rank-number ${
            isTopPick ? 'text-amber' : 'text-charcoal/15'
          } transition-colors duration-300 group-hover:text-amber/60`}>
            {restaurant.rank}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 pt-2">
          <h3 className={`font-display ${isTopPick ? 'text-3xl md:text-4xl' : 'text-2xl md:text-3xl'} text-amber mb-3 transition-colors duration-300 flex items-center gap-2`}>
            {restaurant.name}
            <svg className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </h3>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-sm text-amber font-medium">
              {restaurant.cuisine}
            </span>
            <span className="text-charcoal/20">·</span>
            <span className="text-sm text-charcoal-light">
              {restaurant.location}
            </span>
          </div>

          <a
            href={restaurant.googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-2 text-sm text-charcoal-light hover:text-amber transition-colors duration-300"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="link-underline">Directions</span>
          </a>
        </div>
      </div>
    </Link>
  );
};

export default RestaurantCard;
